import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
    Tray, PaperPlaneTilt, Trash, Star, ArrowsClockwise, MagnifyingGlass,
    Envelope, EnvelopeOpen, Plus, X, ArrowLeft, SignOut, Folder,
    AddressBook, Calendar, CalendarBlank, UserCircle,
} from "@phosphor-icons/react";
import { fmtYmd, fmtYmdTime } from "@/lib/dateFormat";

function MimeBadge({ value }) {
    if (!value) return null;
    return <span className="prc-badge muted text-[10px] ml-1">{value}</span>;
}

function decodeFromForList(from) {
    if (!from) return "-";
    const m = from.match(/(.*)<.*>/);
    return (m ? m[1].trim() : from).replace(/^"|"$/g, "");
}

function CalendarEvent({ ev, highlight = false, compact = false }) {
    const fmt = (iso) => fmtYmdTime(iso, iso || "-");
    return (
        <div className={`prc-card p-3 flex gap-3 items-start ${highlight ? "border-[var(--prc-green)] bg-[rgba(0,255,156,0.05)]" : ""}`} data-testid={`wm-event-${ev.uid || ev.summary || ""}`}>
            <Calendar size={compact ? 16 : 22} className={highlight ? "text-[var(--prc-green)]" : "text-[var(--prc-muted)]"} weight={highlight ? "fill" : "regular"} />
            <div className="flex-1 min-w-0">
                <p className={`${compact ? "text-sm" : "text-base"} font-semibold text-[var(--prc-ink-2)] truncate`}>{ev.summary || "(件名なし)"}</p>
                <p className="text-xs text-[var(--prc-muted)] mt-0.5 font-mono">
                    {fmt(ev.start)}{ev.end ? ` — ${fmt(ev.end)}` : ""}
                </p>
                {ev.location && <p className="text-xs text-[var(--prc-muted)] mt-0.5">📍 {ev.location}</p>}
                {ev.organizer && <p className="text-xs text-[var(--prc-tertiary)] mt-0.5 font-mono break-all">主催: {ev.organizer}</p>}
                {!compact && ev.description && (
                    <p className="text-xs text-[var(--prc-muted)] mt-1 whitespace-pre-wrap line-clamp-3">{ev.description}</p>
                )}
                {!compact && ev.source_subject && (
                    <p className="text-[10px] text-[var(--prc-tertiary)] mt-1 font-mono">
                        from: {ev.source_from} · {ev.source_subject}
                    </p>
                )}
            </div>
            {ev.status && (
                <span className={`prc-badge ${ev.status === "CONFIRMED" ? "ok" : ev.status === "CANCELLED" ? "ng" : "muted"} text-[10px]`}>
                    {ev.status}
                </span>
            )}
        </div>
    );
}

export default function Webmail() {
    const [session, setSession] = useState(null); // null=loading, false=logged out, obj=logged in
    const [loginForm, setLoginForm] = useState({ email: "", password: "" });
    const [loginErr, setLoginErr] = useState("");
    const [loggingIn, setLoggingIn] = useState(false);

    const [folders, setFolders] = useState([]);
    const [folder, setFolder] = useState("INBOX");
    const [messages, setMessages] = useState([]);
    const [total, setTotal] = useState(0);
    const [search, setSearch] = useState("");
    const [selected, setSelected] = useState(null);
    const [loadingMsgs, setLoadingMsgs] = useState(false);
    const [composing, setComposing] = useState(null); // null or {to, subject, body, inReplyTo, references}

    // System-internal sync: Contacts & Calendar
    const [view, setView] = useState("mail"); // "mail" | "contacts" | "calendar"
    const [contacts, setContacts] = useState([]);
    const [contactsQ, setContactsQ] = useState("");
    const [contactsBusy, setContactsBusy] = useState(false);
    const [calendar, setCalendar] = useState({ upcoming: [], past: [], total: 0 });
    const [calendarBusy, setCalendarBusy] = useState(false);

    const checkSession = useCallback(async () => {
        try {
            const { data } = await api.get("/webmail/me");
            setSession(data);
        } catch {
            setSession(false);
        }
    }, []);

    useEffect(() => { checkSession(); }, [checkSession]);

    const loadFolders = useCallback(async () => {
        try {
            const { data } = await api.get("/webmail/folders");
            setFolders(data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    }, []);

    const loadMessages = useCallback(async () => {
        setLoadingMsgs(true);
        try {
            const { data } = await api.get("/webmail/messages", {
                params: { folder, limit: 50, offset: 0, q: search || undefined },
            });
            setMessages(data.messages || []);
            setTotal(data.total || 0);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setLoadingMsgs(false);
        }
    }, [folder, search]);

    useEffect(() => { if (session) { loadFolders(); loadMessages(); } }, [session, loadFolders, loadMessages]);

    const submitLogin = async (e) => {
        e.preventDefault();
        setLoggingIn(true);
        setLoginErr("");
        try {
            const { data } = await api.post("/webmail/login", loginForm);
            setSession(data);
            toast.success("ウェブメールにログインしました");
        } catch (e) {
            setLoginErr(formatApiError(e.response?.data?.detail) || "ログイン失敗");
        } finally {
            setLoggingIn(false);
        }
    };

    const logout = async () => {
        await api.post("/webmail/logout");
        setSession(false);
        setSelected(null);
        setMessages([]);
    };

    const openMessage = async (m) => {
        try {
            const { data } = await api.get("/webmail/message", { params: { folder, uid: m.uid } });
            setSelected(data);
            // Update local list
            setMessages((prev) => prev.map((x) => x.uid === m.uid ? { ...x, unseen: false } : x));
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const flagAction = async (action) => {
        if (!selected) return;
        try {
            await api.post("/webmail/flag", null, { params: { folder, uid: selected.uid, action } });
            await loadMessages();
            if (action === "delete") setSelected(null);
            toast.success("更新しました");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const startCompose = (preset = {}) => {
        setComposing({ to: "", cc: "", subject: "", body: "", in_reply_to: null, references: null, ...preset });
    };

    const replyTo = (msg) => {
        const fromMatch = msg.from?.match(/<(.+?)>/);
        const replyAddr = fromMatch ? fromMatch[1] : msg.from;
        const quoted = (msg.text_body || "").split("\n").map((l) => "> " + l).join("\n");
        startCompose({
            to: replyAddr,
            subject: msg.subject?.startsWith("Re:") ? msg.subject : `Re: ${msg.subject || ""}`,
            body: `\n\n----- ${msg.from} (${msg.date}) -----\n${quoted}`,
            in_reply_to: msg.message_id,
            references: msg.references ? `${msg.references} ${msg.message_id}` : msg.message_id,
        });
    };

    const sendCompose = async (e) => {
        e.preventDefault();
        try {
            const toList = composing.to.split(",").map((s) => s.trim()).filter(Boolean);
            const ccList = composing.cc.split(",").map((s) => s.trim()).filter(Boolean);
            await api.post("/webmail/send", {
                to: toList,
                cc: ccList,
                subject: composing.subject,
                body: composing.body,
                in_reply_to: composing.in_reply_to,
                references: composing.references,
            });
            toast.success("送信しました");
            setComposing(null);
            await loadMessages();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    // ---- Contacts & Calendar loaders ----
    const loadContacts = useCallback(async () => {
        setContactsBusy(true);
        try {
            const { data } = await api.get("/webmail/contacts", { params: { q: contactsQ || undefined, limit: 300 } });
            setContacts(data.contacts || []);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setContactsBusy(false);
        }
    }, [contactsQ]);

    const rebuildContacts = async () => {
        setContactsBusy(true);
        try {
            const { data } = await api.post("/webmail/contacts/rebuild");
            toast.success(`${data.scanned_messages}通 走査、${data.unique_addresses}件のアドレスを抽出`);
            await loadContacts();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setContactsBusy(false);
        }
    };

    const deleteContact = async (email) => {
        if (!window.confirm(`${email} を連絡先から削除しますか？`)) return;
        try {
            await api.delete(`/webmail/contacts/${encodeURIComponent(email)}`);
            await loadContacts();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const loadCalendar = useCallback(async () => {
        setCalendarBusy(true);
        try {
            const { data } = await api.get("/webmail/calendar", { params: { folder: "INBOX", scan_limit: 80 } });
            setCalendar(data || { upcoming: [], past: [], total: 0 });
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setCalendarBusy(false);
        }
    }, []);

    useEffect(() => {
        if (session && view === "contacts") loadContacts();
        if (session && view === "calendar") loadCalendar();
    }, [session, view, loadContacts, loadCalendar]);

    const composeToContact = (email) => {
        startCompose({ to: email });
        setView("mail");
    };

    if (session === null) return <p className="prc-label-overline" data-testid="wm-loading">接続中…</p>;

    if (!session) {
        return (
            <div className="max-w-md mx-auto" data-testid="webmail-login-page">
                <p className="prc-label-overline">ウェブメール</p>
                <h1 className="text-[1.685rem] font-bold tracking-tight">メールにログイン</h1>
                <p className="mt-2 text-sm text-[var(--prc-muted)]">
                    `prc-cmc.site` のメールアカウントでサインインしてください。
                </p>

                <form onSubmit={submitLogin} className="prc-card p-6 mt-6 space-y-3">
                    <div>
                        <label className="prc-label-overline block mb-1">メールアドレス *</label>
                        <input type="email" required className="prc-input"
                               value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                               placeholder="admin@prc-cmc.site" data-testid="wm-login-email" />
                    </div>
                    <div>
                        <label className="prc-label-overline block mb-1">パスワード *</label>
                        <input type="password" required className="prc-input"
                               value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                               data-testid="wm-login-password" />
                    </div>
                    {loginErr && <p className="text-sm text-[var(--prc-red)]" data-testid="wm-login-err">{loginErr}</p>}
                    <button type="submit" disabled={loggingIn} className="prc-btn-primary w-full" data-testid="wm-login-submit">
                        {loggingIn ? "接続中…" : "ログイン"}
                    </button>
                    <p className="text-xs text-[var(--prc-muted)]">
                        IMAP: mail.prc-cmc.site:993 (SSL) / SMTP: 587 (STARTTLS) で接続します。資格情報は8時間有効な暗号化Cookieに保存されます。
                    </p>
                </form>
            </div>
        );
    }

    return (
        <div className="space-y-4 -mx-4 lg:-mx-8 -my-4 lg:-my-8 h-[calc(100vh-3rem)]" data-testid="webmail-page">
            {/* Header bar */}
            <div className="px-6 py-3 border-b border-[var(--prc-border)] bg-[var(--prc-surface)] flex justify-between items-center gap-3 flex-wrap">
                <div className="flex items-center gap-5">
                    <div>
                        <p className="prc-label-overline">ウェブメール</p>
                        <h1 className="text-lg font-bold leading-tight font-mono" data-testid="wm-account">{session.email}</h1>
                    </div>
                    {/* Tab switcher */}
                    <nav className="flex gap-0 border border-[var(--prc-border-strong)] rounded-sm overflow-hidden" data-testid="wm-tabs">
                        {[
                            { k: "mail", label: "メール", Icon: Tray },
                            { k: "contacts", label: "連絡先", Icon: AddressBook },
                            { k: "calendar", label: "カレンダー", Icon: Calendar },
                        ].map((t) => (
                            <button
                                key={t.k}
                                onClick={() => { setView(t.k); setSelected(null); }}
                                className={`px-3 py-2 text-xs font-mono tracking-wider uppercase flex items-center gap-1.5 transition ${view === t.k ? "bg-[var(--prc-green)] text-black" : "bg-[var(--prc-surface-2)] text-[var(--prc-muted)] hover:text-[var(--prc-ink-2)]"}`}
                                data-testid={`wm-tab-${t.k}`}
                            >
                                <t.Icon size={16} weight={view === t.k ? "fill" : "regular"} />
                                {t.label}
                            </button>
                        ))}
                    </nav>
                </div>
                <div className="flex items-center gap-2">
                    {view === "mail" && (
                        <button onClick={loadMessages} className="prc-btn-ghost text-xs inline-flex items-center gap-1" data-testid="wm-refresh">
                            <ArrowsClockwise size={16} />更新
                        </button>
                    )}
                    {view === "contacts" && (
                        <button onClick={rebuildContacts} disabled={contactsBusy} className="prc-btn-ghost text-xs inline-flex items-center gap-1" data-testid="wm-contacts-rebuild">
                            <ArrowsClockwise size={16} />{contactsBusy ? "走査中…" : "受信箱から再構築"}
                        </button>
                    )}
                    {view === "calendar" && (
                        <button onClick={loadCalendar} disabled={calendarBusy} className="prc-btn-ghost text-xs inline-flex items-center gap-1" data-testid="wm-calendar-refresh">
                            <ArrowsClockwise size={16} />{calendarBusy ? "読込中…" : "再走査"}
                        </button>
                    )}
                    <button onClick={() => startCompose()} className="prc-btn-primary text-xs inline-flex items-center gap-1" data-testid="wm-compose-btn">
                        <Plus size={16} />新規メール
                    </button>
                    <button onClick={logout} className="prc-btn-ghost text-xs inline-flex items-center gap-1" data-testid="wm-logout">
                        <SignOut size={16} />ログアウト
                    </button>
                </div>
            </div>

            {view === "contacts" && (
                <div className="px-6 pb-6 h-[calc(100%-4.5rem)] overflow-y-auto" data-testid="wm-contacts-view">
                    <div className="flex items-center gap-2 mb-4 max-w-xl">
                        <MagnifyingGlass size={16} className="text-[var(--prc-muted)] shrink-0" />
                        <input
                            placeholder="連絡先を検索（名前・アドレス）"
                            className="prc-input py-1.5 text-sm"
                            value={contactsQ}
                            onChange={(e) => setContactsQ(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && loadContacts()}
                            data-testid="wm-contacts-search"
                        />
                        <button onClick={loadContacts} className="prc-btn-ghost text-xs">検索</button>
                    </div>
                    {contacts.length === 0 ? (
                        <div className="prc-card p-8 text-center" data-testid="wm-contacts-empty">
                            <AddressBook size={24} className="mx-auto text-[var(--prc-muted)] mb-3" />
                            <p className="text-sm text-[var(--prc-muted)]">
                                連絡先がまだ抽出されていません。<br />
                                右上の「受信箱から再構築」を押すと、既存メールから自動抽出します。
                            </p>
                        </div>
                    ) : (
                        <div className="prc-card overflow-hidden">
                            <table className="prc-table">
                                <thead>
                                    <tr>
                                        <th>名前</th><th>メールアドレス</th><th className="num">件数</th><th>最終やり取り</th><th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {contacts.map((c) => (
                                        <tr key={c.email} data-testid={`wm-contact-${c.email}`}>
                                            <td>
                                                <span className="inline-flex items-center gap-2">
                                                    <UserCircle size={16} className="text-[var(--prc-muted)]" />
                                                    {c.display_name || <span className="text-[var(--prc-tertiary)]">-</span>}
                                                </span>
                                            </td>
                                            <td className="font-mono text-[var(--prc-accent)]">{c.email}</td>
                                            <td className="num">{c.message_count || 0}</td>
                                            <td>{fmtYmd(c.last_seen_at, "")}</td>
                                            <td className="text-right">
                                                <button onClick={() => composeToContact(c.email)} className="prc-btn-ghost text-xs mr-2" data-testid={`wm-contact-write-${c.email}`}>
                                                    <PaperPlaneTilt size={16} /> 作成
                                                </button>
                                                <button onClick={() => deleteContact(c.email)} className="prc-btn-ghost text-xs text-[var(--prc-red)]">
                                                    <Trash size={16} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {view === "calendar" && (
                <div className="px-6 pb-6 h-[calc(100%-4.5rem)] overflow-y-auto space-y-6" data-testid="wm-calendar-view">
                    <section>
                        <p className="prc-label-overline mb-2 !text-[var(--prc-green)]">// UPCOMING</p>
                        {calendar.upcoming.length === 0 ? (
                            <div className="prc-card p-6 text-center text-sm text-[var(--prc-muted)]" data-testid="wm-calendar-upcoming-empty">
                                <CalendarBlank size={24} className="mx-auto mb-2" />
                                予定された iCalendar 招待はありません
                            </div>
                        ) : (
                            <ul className="space-y-2" data-testid="wm-calendar-upcoming">
                                {calendar.upcoming.map((ev, i) => <CalendarEvent key={`u${i}`} ev={ev} highlight />)}
                            </ul>
                        )}
                    </section>
                    <section>
                        <p className="prc-label-overline mb-2">// PAST</p>
                        {calendar.past.length === 0 ? (
                            <p className="text-xs text-[var(--prc-muted)]">過去のイベントはありません</p>
                        ) : (
                            <ul className="space-y-2" data-testid="wm-calendar-past">
                                {calendar.past.map((ev, i) => <CalendarEvent key={`p${i}`} ev={ev} />)}
                            </ul>
                        )}
                    </section>
                </div>
            )}

            {view === "mail" && (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 px-6 pb-6 h-[calc(100%-4.5rem)]">
                {/* Folders pane */}
                <aside className="hidden lg:block lg:col-span-2 border border-[var(--prc-border)] bg-[var(--prc-surface)] overflow-y-auto" data-testid="wm-folders">
                    <div className="p-3 border-b border-[var(--prc-border)]">
                        <p className="prc-label-overline">フォルダ</p>
                    </div>
                    <ul className="divide-y divide-[var(--prc-border)]">
                        {folders.length === 0 && <li className="p-3 text-xs text-[var(--prc-muted)]">読込中…</li>}
                        {folders.map((f) => (
                            <li key={f.name}>
                                <button
                                    onClick={() => { setFolder(f.name); setSelected(null); }}
                                    className={`w-full text-left p-3 text-sm flex items-center justify-between hover:bg-[var(--prc-bg)] ${folder === f.name ? "bg-[var(--prc-bg)] font-semibold" : ""}`}
                                    data-testid={`wm-folder-${f.name}`}
                                >
                                    <span className="flex items-center gap-2 truncate">
                                        {f.name === "INBOX" ? <Tray size={16} /> : <Folder size={16} />}
                                        <span className="truncate">{f.name === "INBOX" ? "受信箱" : f.name}</span>
                                    </span>
                                    {f.unseen > 0 && <span className="prc-badge info text-[10px]">{f.unseen}</span>}
                                </button>
                            </li>
                        ))}
                    </ul>
                </aside>

                {/* Message list */}
                <div className="lg:col-span-4 border border-l-0 border-[var(--prc-border)] bg-[var(--prc-surface)] overflow-hidden flex flex-col" data-testid="wm-msg-list-pane">
                    <div className="p-3 border-b border-[var(--prc-border)] flex items-center gap-2">
                        <MagnifyingGlass size={16} className="text-[var(--prc-muted)] shrink-0" />
                        <input
                            placeholder="検索 (件名/差出人/本文)"
                            className="prc-input py-1.5 text-sm flex-1"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && loadMessages()}
                            data-testid="wm-search"
                        />
                    </div>
                    <ul className="flex-1 overflow-y-auto divide-y divide-[var(--prc-border)]" data-testid="wm-msg-list">
                        {loadingMsgs && <li className="p-4 text-sm text-[var(--prc-muted)]">読込中…</li>}
                        {!loadingMsgs && messages.length === 0 && <li className="p-6 text-center text-sm text-[var(--prc-muted)]" data-testid="wm-empty">メールがありません</li>}
                        {messages.map((m) => (
                            <li key={m.uid}>
                                <button
                                    onClick={() => openMessage(m)}
                                    className={`w-full text-left p-3 hover:bg-[var(--prc-bg)] ${selected?.uid === m.uid ? "bg-[var(--prc-bg)]" : ""} ${m.unseen ? "border-l-2 border-[var(--prc-accent)]" : ""}`}
                                    data-testid={`wm-msg-${m.uid}`}
                                >
                                    <div className="flex justify-between items-start gap-2">
                                        <p className={`text-sm truncate ${m.unseen ? "font-bold" : "font-medium"}`}>{decodeFromForList(m.from)}</p>
                                        <p className="text-[10px] text-[var(--prc-tertiary)] shrink-0">{fmtYmdTime(m.date, (m.date || "").substring(0, 16))}</p>
                                    </div>
                                    <p className={`text-sm truncate mt-0.5 ${m.unseen ? "font-semibold" : ""}`}>
                                        {m.flagged && <Star size={16} className="inline text-amber-500 mr-1" weight="fill" />}
                                        {m.subject || "(件名なし)"}
                                    </p>
                                </button>
                            </li>
                        ))}
                    </ul>
                    <div className="p-2 border-t border-[var(--prc-border)] text-xs text-[var(--prc-muted)] text-center">
                        {messages.length} / {total} 件
                    </div>
                </div>

                {/* Message detail */}
                <div className="lg:col-span-6 border border-l-0 border-[var(--prc-border)] bg-[var(--prc-surface)] overflow-hidden flex flex-col" data-testid="wm-msg-detail-pane">
                    {!selected ? (
                        <div className="flex-1 flex items-center justify-center text-sm text-[var(--prc-muted)]" data-testid="wm-no-selection">
                            メールを選択してください
                        </div>
                    ) : (
                        <>
                            <div className="p-4 border-b border-[var(--prc-border)]">
                                <div className="flex justify-between items-start gap-2 mb-2">
                                    <h2 className="text-lg font-bold tracking-tight" data-testid="wm-detail-subject">{selected.subject || "(件名なし)"}</h2>
                                    <button onClick={() => setSelected(null)} className="lg:hidden text-[var(--prc-muted)]" data-testid="wm-back"><ArrowLeft size={16} /></button>
                                </div>
                                <div className="grid grid-cols-[80px_1fr] gap-y-1 text-xs">
                                    <span className="prc-label-overline">From</span><span className="font-mono break-all">{selected.from}</span>
                                    <span className="prc-label-overline">To</span><span className="font-mono break-all">{selected.to}</span>
                                    {selected.cc && (<><span className="prc-label-overline">Cc</span><span className="font-mono break-all">{selected.cc}</span></>)}
                                    <span className="prc-label-overline">Date</span><span>{selected.date}</span>
                                </div>
                                {selected.attachments?.length > 0 && (
                                    <div className="mt-2 flex flex-wrap gap-1">
                                        {selected.attachments.map((a, i) => (
                                            <span key={i} className="prc-badge muted text-xs" data-testid={`wm-att-${i}`}>📎 {a.filename || "添付"} ({Math.round(a.size / 1024)}KB)</span>
                                        ))}
                                    </div>
                                )}
                                {selected.calendar_events?.length > 0 && (
                                    <div className="mt-3 space-y-1.5" data-testid="wm-msg-calendar-events">
                                        <p className="prc-label-overline !text-[var(--prc-green)]">// CALENDAR INVITE</p>
                                        {selected.calendar_events.map((ev, i) => <CalendarEvent key={i} ev={ev} compact />)}
                                    </div>
                                )}
                                <div className="mt-3 flex gap-2 flex-wrap">
                                    <button onClick={() => replyTo(selected)} className="prc-btn-primary text-xs inline-flex items-center gap-1" data-testid="wm-reply-btn">
                                        <PaperPlaneTilt size={16} />返信
                                    </button>
                                    <button onClick={() => flagAction(selected.unseen ? "read" : "unread")} className="prc-btn-ghost text-xs inline-flex items-center gap-1" data-testid="wm-toggle-read">
                                        {selected.unseen ? <EnvelopeOpen size={16} /> : <Envelope size={16} />}
                                        {selected.unseen ? "既読にする" : "未読にする"}
                                    </button>
                                    <button onClick={() => flagAction("flag")} className="prc-btn-ghost text-xs inline-flex items-center gap-1" data-testid="wm-flag-btn">
                                        <Star size={16} />フラグ
                                    </button>
                                    <button onClick={() => { if (window.confirm("このメールを削除しますか？")) flagAction("delete"); }} className="prc-btn-ghost text-xs inline-flex items-center gap-1 text-[var(--prc-red)] border-[var(--prc-red)]/40" data-testid="wm-delete-btn">
                                        <Trash size={16} />削除
                                    </button>
                                </div>
                            </div>
                            <div className="flex-1 overflow-y-auto p-4">
                                {selected.html_body ? (
                                    <iframe
                                        srcDoc={selected.html_body}
                                        sandbox=""
                                        className="w-full h-full border-0"
                                        title="message"
                                        data-testid="wm-html-body"
                                    />
                                ) : (
                                    <pre className="text-sm whitespace-pre-wrap break-words font-sans leading-relaxed" data-testid="wm-text-body">
                                        {selected.text_body}
                                    </pre>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
            )}

            {/* Compose dialog */}
            {composing && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-end lg:items-center justify-center" data-testid="wm-compose-dialog">
                    <form onSubmit={sendCompose} className="prc-card w-full lg:max-w-2xl p-5 max-h-[90vh] overflow-auto rounded-t-lg lg:rounded-md">
                        <div className="flex justify-between items-center mb-3">
                            <h3 className="font-bold">メール作成</h3>
                            <button type="button" onClick={() => setComposing(null)} className="text-[var(--prc-muted)]" data-testid="wm-compose-close"><X size={24} /></button>
                        </div>
                        <div className="space-y-2">
                            <div>
                                <label className="prc-label-overline block mb-1">To * (カンマ区切り)</label>
                                <input required className="prc-input" value={composing.to} onChange={(e) => setComposing({ ...composing, to: e.target.value })} data-testid="wm-compose-to" />
                            </div>
                            <div>
                                <label className="prc-label-overline block mb-1">Cc</label>
                                <input className="prc-input" value={composing.cc} onChange={(e) => setComposing({ ...composing, cc: e.target.value })} data-testid="wm-compose-cc" />
                            </div>
                            <div>
                                <label className="prc-label-overline block mb-1">件名 *</label>
                                <input required className="prc-input" value={composing.subject} onChange={(e) => setComposing({ ...composing, subject: e.target.value })} data-testid="wm-compose-subject" />
                            </div>
                            <div>
                                <label className="prc-label-overline block mb-1">本文 *</label>
                                <textarea required className="prc-input min-h-48 font-mono text-sm" value={composing.body} onChange={(e) => setComposing({ ...composing, body: e.target.value })} data-testid="wm-compose-body" />
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 mt-4">
                            <button type="button" onClick={() => setComposing(null)} className="prc-btn-ghost" data-testid="wm-compose-cancel">キャンセル</button>
                            <button type="submit" className="prc-btn-primary inline-flex items-center gap-1" data-testid="wm-compose-send">
                                <PaperPlaneTilt size={16} />送信
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}
